import React, { useCallback, useEffect, useRef, useState } from "react";
import Button from "./Button/Button";

interface MenuButtonImage {
  imagePosition?: "left" | "right";
  imageSrc?: string;
}

interface MenuComponentProps {
  menuTitle: string | JSX.Element;
  menuList: JSX.Element;
  customImage?: MenuButtonImage;
  customButtonCss?: string;
  customMenuListCss?: string;
  alignDropdownToCorner?: boolean;
  isOpen: boolean;
  toggleMenu?: () => void;
  closeMenu: () => void;
}

const MenuComponent: React.FC<MenuComponentProps> = ({
  menuTitle,
  menuList,
  customImage,
  customButtonCss,
  customMenuListCss,
  alignDropdownToCorner = false,
  isOpen,
  closeMenu,
  toggleMenu,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [cornerDropdownStyle, setCornerDropdownStyle] = useState<
    React.CSSProperties
  >({});

  const updateCornerDropdownPosition = useCallback(() => {
    const trigger = menuRef.current?.querySelector("button");
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    setCornerDropdownStyle({
      position: "fixed",
      top: rect.bottom,
      right: window.innerWidth - rect.right,
    });
  }, []);

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      closeMenu();
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isOpen || !alignDropdownToCorner) return;

    updateCornerDropdownPosition();
    window.addEventListener("resize", updateCornerDropdownPosition);
    window.addEventListener("scroll", updateCornerDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateCornerDropdownPosition);
      window.removeEventListener("scroll", updateCornerDropdownPosition, true);
    };
  }, [alignDropdownToCorner, isOpen, updateCornerDropdownPosition]);

  const dropdownClassName = [
    "bg-white h-auto w-40 z-[100] rounded-md border border-gray1 border-b-2 border-b-gray-300",
    alignDropdownToCorner
      ? "fixed origin-top-right"
      : "absolute top-full right-0 origin-top-right",
    customMenuListCss,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div ref={menuRef} className="relative w-fit shrink-0">
      <Button
        onClick={(e) => {
          e.preventDefault();
          toggleMenu?.();
        }}
        className={`flex justify-center hover:underline items-center gap-1 w-full px-1 py-1 rounded-[2px] ${customButtonCss}`}
      >
        {customImage?.imagePosition === "left" && (
          <img
            src={customImage?.imageSrc}
            alt="chevron-down"
            className="h-4 w-4"
          />

        )}

        {menuTitle}

        {customImage?.imagePosition === "right" && (
          <img
            src={customImage?.imageSrc}
            alt="menu-button-icon"
            className="h-4 w-4"
          />
        )}
      </Button>

      {isOpen && (
        <div
          className={dropdownClassName}
          style={alignDropdownToCorner ? cornerDropdownStyle : undefined}
          onClick={closeMenu}
        >
          {menuList}
        </div>
      )}
    </div>
  );
};

export default MenuComponent;
